import React, { useEffect, useRef } from 'react';
import useAsyncEffect from 'use-async-effect';
import { useMytaverse } from '../../../../../providers/MytaverseProvider';
import { useGameInitialization } from './hooks';
import { IGameCast, IUseGamecastProps } from './interfaces';
import { getUserBrowser } from './helpers';
import { WebsocketAction } from '../../../../../interfaces/webSocketConnectionInfo';
import { useConference } from '../Dolby';
import { MytaverseLogger } from '../../../../../helpers/logger';
import { useLocation } from 'react-router-dom';
import ROUTES from '../../../../../constants/routes';
import { useEventContext } from '../../../../../ek/providers/EventProvider';
import {
  EventParticipantStateType,
  IEventParticipant,
} from '../../../../../interfaces/eventParticipant';
import {
  IXR_HR_BRAND_UNIFORMS,
  IXRHrCredStatusTypes,
} from '../../../../../interfaces/profile';
import { useConferenceState } from '../../../../../hooks/conferenceContext';
import { IStartStreamSessionOutput } from '../../../../../interfaces/gamecast';
import { IInitMessage } from '../Pureweb/interfaces';
import { AnalyticsType } from '../../../../../interfaces/analytics';

import { default as gameliftStreamsLogger } from './logger';

const GAMECAST_VIDEO_TAG_ID = 'gamecastVideoTag';

export const useGamecast = ({
  currentEventId,
  currentParticipant,
  websocketSessionId,
  currentRoom,
  gameSound,
  setGameCastStreamId,
  setGameCastStreamRegion,
  initMessageHandler,
  gameCastStreamId,
  gameCastStreamRegion,
  gameCastStreamRequestSent,
  setGameCastStreamRequestSent,
  setInitMessageSent,
  setStreamingProvider,
  setIsGamecastCrash,
  setCurrentRoom,
  isGamecastCrash,
  gamecastStats,
  currentEvent,
  trackAnalytics,
  sessionKey
}: IUseGamecastProps): {
  isStreamLoading: boolean;
  isStreamLoaded: boolean;
  setIsStreamLoaded: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
  setIsStreamLoading: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  closeGamecastStream: () => void;
  abortGamecastConnection: () => void;
  gamecastClientRef: React.MutableRefObject<IGameCast | null>;
  processActiveStreamSession: (
    gamecastStreamSession: IStartStreamSessionOutput,
  ) => Promise<{
    isStarted: boolean;
    isAborted: boolean;
  }>;
  trackAnalytics: (type: AnalyticsType, payload?: any) => void;
  sendMytaverseActions: (currentEventParticipant: IEventParticipant) => void;
  initializeGamecastStream: (
    participantId: string,
    websocketSessionId: string,
    initMessageHandler: () => Promise<IInitMessage | null>,
    region: string,
    launchArgs: string[] | null,
  ) => Promise<
    | {
        isStarted: boolean;
      }
    | { isAborted: boolean; gamecastRTCStatsInterval: any }
  >;
} => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const connectionTimer = useRef<number | null>(0);
  const browserType = getUserBrowser();
  const [isStreamLoading, setIsStreamLoading] = React.useState(false);
  const [isStreamLoaded, setIsStreamLoaded] = React.useState(false);
  const { conference } = useConference();
  const { sendJSONMessageToWebSocket, user, isDebugMode } = useMytaverse();
  const { pathname } = useLocation();
  const { isEventRun, launchArgs } = useEventContext();
  const { activeSpeakerDeviceId } = useConferenceState();

  const sendMytaverseActions = React.useCallback(
    (currentEventParticipant: IEventParticipant) => {
      const eventParticipantState: EventParticipantStateType =
        currentEventParticipant?.state
          ? JSON.parse(currentEventParticipant?.state)
          : '';
      if (
        user.hrData.ixrCredentialStatus === IXRHrCredStatusTypes.FT &&
        IXR_HR_BRAND_UNIFORMS.includes(user.hrData?.brand?.toUpperCase()) &&
        currentEventParticipant?.isHubMultiplayer &&
        !eventParticipantState?.bIsCelebrationMoment
      ) {
        console.log('Celebratory Moment Active');
        sendJSONMessageToWebSocket({
          action: WebsocketAction.MytaverseAction,
          tag: 'MytaEvent.Action.Tutorial.Start',
          data: 'Tutorial.iXR.CelebratoryMoment',
        });
      }
    },
    [sendJSONMessageToWebSocket],
  );

  const {
    initializeGamecastStream,
    closeGamecastStream,
    abortGamecastConnection,
    handleReconnectGamecast,
    gamecastClientRef,
    processActiveStreamSession,
  } = useGameInitialization({
    browserType,
    streamId: gameCastStreamId,
    currentParticipant,
    setStreamingProvider,
    setIsGamecastCrash,
    gamecastStats,
    currentRoom,
    sessionKey
  });

  useAsyncEffect(async () => {
    if (
      isStreamLoading ||
      !currentParticipant ||
      gameCastStreamRequestSent ||
      !gameCastStreamId ||
      !gameCastStreamRegion
    ) {
      return;
    }

    if (isDebugMode) {
      const initMessage = await initMessageHandler();
      console.log({ initMessage });
      setInitMessageSent(true);
      setIsStreamLoaded(true);
      return;
    }

    try {
      setIsStreamLoading(true);
      if (!initMessageHandler) {
        return;
      }

      connectionTimer.current = new Date().getTime();

      setGameCastStreamRequestSent(true);
      setInitMessageSent(true);

      videoRef.current = document.getElementById(
        GAMECAST_VIDEO_TAG_ID,
      ) as HTMLVideoElement;

      trackAnalytics(AnalyticsType.StartGameLiftStreamsInit);
      await initializeGamecastStream(
        currentParticipant.participantId,
        websocketSessionId,
        initMessageHandler,
        gameCastStreamRegion,
        launchArgs,
      );
    } catch (error) {
      setIsStreamLoading(false);
    }
  }, [
    currentParticipant,
    initMessageHandler,
    gameCastStreamRequestSent,
    gameCastStreamRegion,
    initializeGamecastStream,
    isDebugMode,
    isStreamLoading,
    gameCastStreamId,
  ]);

  useEffect(() => {
    return () => {
      setGameCastStreamId('');
      setGameCastStreamRegion('');
    };
  }, []);

  useEffect(() => {
    if (
      (!currentEvent?.enablePureGamecast && !currentRoom) ||
      !isStreamLoaded ||
      !currentEventId ||
      !isEventRun ||
      pathname !== ROUTES.EVENT_PAGE(currentEventId)
    ) {
      return;
    }

    const videoElement = document.getElementById(GAMECAST_VIDEO_TAG_ID);

    if (!videoElement) {
      return;
    }

    videoElement.style.position = 'absolute';
    videoElement.style.left = '0';
    videoElement.style.top = '0';
    videoElement.style.zIndex = '-1';
    videoElement.style.visibility = 'visible';
    videoElement.style.width = '100%';
    videoElement.style.height = '100vh';
    videoElement.style.maxHeight = '100%';
  }, [
    currentRoom,
    isStreamLoaded,
    videoRef,
    currentEventId,
    pathname,
    isEventRun,
  ]);

  useAsyncEffect(async () => {
    if (videoRef.current && currentRoom && isEventRun) {
      try {
        //@ts-ignore
        await videoRef.current.setSinkId(activeSpeakerDeviceId);
      } catch (e) {
        console.error('Error setting audio output:', e);
      }

      videoRef.current.muted = false;
      videoRef.current.volume = gameSound;
    }
  }, [
    gameSound,
    videoRef,
    conference,
    currentRoom,
    isEventRun,
    activeSpeakerDeviceId,
    currentEvent,
  ]);

  useAsyncEffect(async () => {
    if (!currentRoom) {
      return;
    }

    if (connectionTimer.current) {
      const connectionTime = new Date().getTime() - connectionTimer.current;

      gameliftStreamsLogger.log(
        `GAMELIFT STREAMS CONNECTION TIME ${connectionTime / 1000}s`,
      );
      connectionTimer.current = null;
    }
  }, [currentRoom]);

  useAsyncEffect(() => {
    const browserType = getUserBrowser();

    MytaverseLogger.log(`ENTERING BY ${browserType} BROWSER`);
  }, []);

  useAsyncEffect(async () => {
    if (
      !isGamecastCrash ||
      !gameCastStreamRegion ||
      !currentParticipant ||
      !initMessageHandler
    ) {
      return;
    }
    setCurrentRoom(null);

    await handleReconnectGamecast({
      region: gameCastStreamRegion,
      participantId: currentParticipant.participantId,
      websocketSessionId,
      initMessageHandler,
      launchArgs,
    });

    setTimeout(() => {
      setIsGamecastCrash(false);
    }, 20000);
  }, [isGamecastCrash, initMessageHandler]);

  return {
    isStreamLoading,
    isStreamLoaded,
    setIsStreamLoaded,
    setIsStreamLoading,
    videoRef,
    closeGamecastStream,
    abortGamecastConnection,
    gamecastClientRef,
    processActiveStreamSession,
    sendMytaverseActions,
    initializeGamecastStream,
    trackAnalytics,
  };
};
